import { useState, useEffect, useCallback } from 'react';
import { playAchievement } from '@/utils/audio';

// Dollar bill emoji and money symbols
const MONEY_SYMBOLS = ['💵', '💰', '🤑', '💸', '💲', '🏦', '💎'];

// Single bill component
const Bill = ({ style, symbol }) => (
  <div
    className="fixed pointer-events-none text-3xl z-50 animate-fall"
    style={style}
  >
    {symbol}
  </div>
);

// Dollar Bill Rain Effect Component
export function DollarBillRain({ trigger, duration = 3000, density = 30, onComplete }) {
  const [bills, setBills] = useState([]);
  const [isActive, setIsActive] = useState(false);

  const createBill = useCallback((index) => {
    const symbol = MONEY_SYMBOLS[Math.floor(Math.random() * MONEY_SYMBOLS.length)];
    const left = Math.random() * 100;
    const delay = Math.random() * 1000;
    const fallDuration = 2000 + Math.random() * 2000;
    const rotation = Math.random() * 360;
    const scale = 0.8 + Math.random() * 0.6;

    return {
      id: `${Date.now()}-${index}`,
      symbol,
      style: {
        left: `${left}%`,
        top: '-50px',
        transform: `rotate(${rotation}deg) scale(${scale})`,
        animation: `fall ${fallDuration}ms ease-in forwards`,
        animationDelay: `${delay}ms`,
        opacity: 0.9,
      },
    };
  }, []);

  useEffect(() => {
    if (trigger && !isActive) {
      setIsActive(true);
      playAchievement();
      
      // Create initial bills
      const newBills = Array.from({ length: density }, (_, i) => createBill(i));
      setBills(newBills);

      // Clean up after animation
      const timeout = setTimeout(() => {
        setBills([]);
        setIsActive(false);
        onComplete?.();
      }, duration + 4000);

      return () => clearTimeout(timeout);
    }
  }, [trigger, isActive, density, duration, createBill, onComplete]);

  if (bills.length === 0) return null;

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-50">
      {bills.map((bill) => (
        <Bill key={bill.id} style={bill.style} symbol={bill.symbol} />
      ))}
      <style>{`
        @keyframes fall {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
}

// Streak Milestone Celebration Component
export function StreakCelebration({ streakCount, previousStreak, onComplete }) {
  const [showCelebration, setShowCelebration] = useState(false);
  const [milestone, setMilestone] = useState(null);

  const MILESTONES = {
    7: { title: 'PAPER CHASER', emoji: '📄', message: "You're getting started!" },
    14: { title: 'BOUNCE BACK EARNED', emoji: '🔄', message: 'You earned a streak freeze!' },
    30: { title: 'BAG GETTER', emoji: '💰', message: 'Securing the bag consistently!' },
    90: { title: 'HEAVY HITTER', emoji: '🔥', message: 'Real discipline now!' },
    100: { title: 'CENTURY', emoji: '💯', message: '100 days strong!' },
    365: { title: 'MOGUL STATUS', emoji: '👑', message: 'You run this!' },
  };

  useEffect(() => {
    // Check if we just hit a milestone
    for (const [days, data] of Object.entries(MILESTONES)) {
      const daysNum = parseInt(days);
      if (streakCount >= daysNum && previousStreak < daysNum) {
        setMilestone({ days: daysNum, ...data });
        setShowCelebration(true);
        break;
      }
    }
  }, [streakCount, previousStreak]);

  const handleComplete = () => {
    setShowCelebration(false);
    setMilestone(null);
    onComplete?.();
  };

  if (!showCelebration || !milestone) return null;

  return (
    <>
      <DollarBillRain trigger={true} density={40} onComplete={handleComplete} />
      
      {/* Milestone Banner */}
      <div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
        <div className="bg-black/80 backdrop-blur-sm rounded-2xl p-8 text-center animate-bounce-in">
          <div className="text-6xl mb-4">{milestone.emoji}</div>
          <h2 className="text-4xl font-anton text-guap-green mb-2">
            {milestone.title}
          </h2>
          <p className="text-xl text-white mb-2">{milestone.days} Day Streak!</p>
          <p className="text-gray-400">{milestone.message}</p>
        </div>
      </div>
      
      <style>{`
        @keyframes bounce-in {
          0% { transform: scale(0); opacity: 0; }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); opacity: 1; }
        }
        .animate-bounce-in {
          animation: bounce-in 0.5s ease-out forwards;
        }
      `}</style>
    </>
  );
}

// Quick celebration for transactions (smaller effect)
export function TransactionCelebration({ show, type = 'expense', onComplete }) {
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    if (show) {
      const symbols = type === 'income' ? ['💵', '💰', '🤑'] : ['✨', '⭐', '💫'];
      const newParticles = Array.from({ length: 10 }, (_, i) => ({
        id: i,
        symbol: symbols[Math.floor(Math.random() * symbols.length)],
        style: {
          position: 'fixed',
          left: `${45 + Math.random() * 10}%`,
          top: `${45 + Math.random() * 10}%`,
          fontSize: '1.5rem',
          animation: `particle-burst 1s ease-out forwards`,
          animationDelay: `${i * 50}ms`,
          '--x': `${(Math.random() - 0.5) * 200}px`,
          '--y': `${(Math.random() - 0.5) * 200}px`,
        },
      }));
      
      setParticles(newParticles);
      
      const timeout = setTimeout(() => {
        setParticles([]);
        onComplete?.();
      }, 1500);
      
      return () => clearTimeout(timeout);
    }
  }, [show, type, onComplete]);

  if (particles.length === 0) return null;

  return (
    <div className="pointer-events-none z-50">
      {particles.map((p) => (
        <div key={p.id} style={p.style}>{p.symbol}</div>
      ))}
      <style>{`
        @keyframes particle-burst {
          0% {
            transform: translate(0, 0) scale(1);
            opacity: 1;
          }
          100% {
            transform: translate(var(--x), var(--y)) scale(0);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
}

export default DollarBillRain;
